import { createContext, useContext } from 'react';

import { useAuth } from '@/auth/auth-context';
import { parseGraphQLResponse, useWorkspaceQuery } from '@/graphql';
import { FullPageLoader } from '@/components/loader';

import { Account, accountSchema } from './types';

interface WorkspaceContext {
  accounts: Account[];
  user: { email: string; emailVerified: boolean } | null;
}

const defaultContextValue = Symbol();

const WorkspaceContext = createContext<WorkspaceContext | typeof defaultContextValue>(
  defaultContextValue,
);

export const useWorkspaceContext = () => {
  const context = useContext(WorkspaceContext);
  if (context === defaultContextValue) {
    throw new Error('useWorkspaceContext must be used within a WorkspaceContextProvider');
  }
  return context;
};

interface WorkspaceContextProviderProps {
  children: React.ReactNode;
}

export const WorkspaceContextProvider = ({ children }: WorkspaceContextProviderProps) => {
  const { user } = useAuth();

  const { data, loading } = useWorkspaceQuery({
    fetchPolicy: 'cache-and-network',
    skip: user === null || !user.emailVerified,
  });

  if (loading) {
    return <FullPageLoader />;
  }

  const workspaceData = {
    accounts: (data?.accounts ?? []).map((input) => parseGraphQLResponse(input, accountSchema)),
    user:
      data?.me !== undefined
        ? { email: data.me?.email ?? '', emailVerified: user?.emailVerified ?? false }
        : user,
  };

  return <WorkspaceContext.Provider value={workspaceData}>{children}</WorkspaceContext.Provider>;
};
